import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  verifyEmail as apiVerifyEmail,
  resendVerificationEmail as apiResendVerification,
} from '../api/auth.ts';
import { PlatformApiError } from '../api/client.ts';
import { AuthPageFrame } from '../components/AuthPageFrame.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS_ON_LOWEST } from '../styles/forms.ts';

/**
 * Consumes the `?token=...` query param against `POST /v1/auth/verify-email`.
 * That endpoint both flips the account's `email_verified` flag and mints a
 * tenant session cookie in the same round-trip — so on success we can drop
 * the user straight into `/dashboard` without a second sign-in.
 *
 * States:
 *   - `missing-token` — page opened without `?token=` in the URL. Ask the
 *     user to use the link from the email.
 *   - `pending`       — POST in flight.
 *   - `success`       — verified; render a redirect to `/dashboard`.
 *   - `error`         — INVALID_VERIFICATION_TOKEN / VERIFICATION_TOKEN_*;
 *                       surface a resend form so the user can recover
 *                       without going back to `/register`.
 *
 */

type VerifyState =
  | { readonly kind: 'missing-token' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'success' }
  | { readonly kind: 'error'; readonly message: string };

export function VerifyEmail(): JSX.Element {
  const [search] = useSearchParams();
  const token = search.get('token');
  const { fetchImpl, refresh } = useSession();
  const { error: showErrorToast } = useToast();
  const t = useT();
  const [state, setState] = useState<VerifyState>(
    token === null || token.length === 0 ? { kind: 'missing-token' } : { kind: 'pending' },
  );
  const [resendEmail, setResendEmail] = useState('');
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [resendAck, setResendAck] = useState(false);

  useEffect(() => {
    if (token === null || token.length === 0) {
      return;
    }

    let cancelled = false;
    setState({ kind: 'pending' });

    (async () => {
      try {
        await apiVerifyEmail(token, fetchImpl);
        if (cancelled) {
          return;
        }
        // Hydrate the session context so <RequireAuth> lets us through on
        // the next render. Without this refresh the user would be bounced
        // back to `/login` because the provider still has `unauthenticated`
        // state from the initial probe.
        await refresh();
        if (cancelled) {
          return;
        }
        setState({ kind: 'success' });
      } catch (err) {
        if (cancelled) {
          return;
        }
        const message =
          err instanceof PlatformApiError
            ? `${err.code}: ${err.message}`
            : coerceErrorMessage(err);
        setState({ kind: 'error', message });
        showErrorToast(message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, fetchImpl, refresh, showErrorToast]);

  async function handleResend(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const trimmed = resendEmail.trim();
    if (trimmed.length === 0) {
      return;
    }
    setResendSubmitting(true);
    try {
      await apiResendVerification({ email: trimmed }, fetchImpl);
      // Backend never discloses whether the email matched, so the UI only
      // acknowledges receipt. Do not clear the input — preserving it helps
      // the user re-read what they typed.
      setResendAck(true);
    } catch (err) {
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      showErrorToast(message);
    } finally {
      setResendSubmitting(false);
    }
  }

  return (
    <AuthPageFrame
      eyebrow={t('verify.eyebrow')}
      title={t('verify.subtitle')}
      description={t('verify.description')}
      maxWidthClassName="max-w-[500px]"
    >

        {state.kind === 'missing-token' ? (
          <p className="text-sm text-error" data-testid="verify-missing-token">
            {t('verify.missing_token')}
          </p>
        ) : null}

        {state.kind === 'pending' ? (
          <p className="text-sm text-outline" data-testid="verify-pending" role="status">
            {t('verify.pending')}
          </p>
        ) : null}

        {state.kind === 'success' ? (
          // POST /verify-email has already minted the session cookie; jump
          // straight to the dashboard. `replace` swallows the verify-email
          // URL from history so a back-nav does not re-fire the consume.
          <Navigate to="/dashboard" replace />
        ) : null}

        {state.kind === 'error' ? (
          <div className="flex flex-col gap-4">
            <div
              className="flex flex-col gap-2 rounded-lg bg-error-container p-6 text-on-error-container"
              data-testid="verify-error"
              role="alert"
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.1em]">
                {t('verify.error.title')}
              </span>
              <p className="text-sm">{state.message}</p>
            </div>

            {resendAck ? (
              <div
                className="flex flex-col gap-2 rounded-lg bg-surface-container-low p-6"
                data-testid="verify-resend-ack"
              >
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-primary">
                  {t('verify.resend.ack.title')}
                </span>
                <p className="text-sm text-on-surface">{t('verify.resend.ack.body')}</p>
                <Link
                  to="/login"
                  className="mt-2 inline-flex w-fit items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-low"
                >
                  {t('common.back_to_login')}
                </Link>
              </div>
            ) : (
              <form
                className="flex flex-col gap-4 rounded-lg bg-surface-container-low p-6"
                onSubmit={handleResend}
                data-testid="verify-resend-form"
              >
                <label className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                    {t('verify.resend.label')}
                  </span>
                  <input
                    type="email"
                    autoComplete="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.currentTarget.value)}
                    disabled={resendSubmitting}
                    required
                    data-testid="verify-resend-email"
                    className={INPUT_CLASS_ON_LOWEST}
                  />
                </label>
                <button
                  type="submit"
                  disabled={resendSubmitting || resendEmail.trim().length === 0}
                  data-testid="verify-resend-submit"
                  className="inline-flex items-center justify-center rounded-xl bg-primary-container px-6 py-3 text-sm font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
                >
                  {resendSubmitting ? t('verify.resend.submitting') : t('verify.resend.submit')}
                </button>
              </form>
            )}

            <Link
              to="/login"
              className="text-xs text-outline hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
            >
              {t('common.back_to_login')}
            </Link>
          </div>
        ) : null}
    </AuthPageFrame>
  );
}
